import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FileSpreadsheetIcon, ChevronDownIcon, CloudIcon, ClockIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
interface ExportButtonProps {
  reportName: string;
  rowCount: number;
}
export function ExportButton({
  reportName,
  rowCount
}: ExportButtonProps) {
  const {
    queueExport,
    driveSynced
  } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);
  const handleExport = (format: 'xlsx' | 'csv') => {
    queueExport(reportName, rowCount, format);
    setOpen(false);
  };
  return <div className="relative" ref={ref} data-id="element-203">
      <button type="button" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} className="inline-flex h-9 items-center gap-2 rounded-full bg-navy-900 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-800" data-id="element-204">
        <FileSpreadsheetIcon className="h-4 w-4" data-id="element-205" />
        Export
        <ChevronDownIcon className="h-3.5 w-3.5" data-id="element-206" />
      </button>
      <AnimatePresence data-id="element-207">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} role="menu" className="card-float-lg absolute right-0 z-20 mt-1.5 w-64 rounded-xl bg-white p-1.5" data-id="element-208">
            <button type="button" role="menuitem" onClick={() => handleExport('xlsx')} className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-navy-50" data-id="element-209">
              <FileSpreadsheetIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" data-id="element-210" />
              <span data-id="element-211">
                <span className="block text-sm font-medium text-navy-900" data-id="element-212">
                  Excel (.xlsx)
                </span>
                <span className="block text-xs text-navy-400" data-id="element-213">
                  Formatted workbook
                </span>
              </span>
            </button>
            <button type="button" role="menuitem" onClick={() => handleExport('csv')} className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-navy-50" data-id="element-214">
              <FileSpreadsheetIcon className="mt-0.5 h-4 w-4 shrink-0 text-navy-400" data-id="element-215" />
              <span data-id="element-216">
                <span className="block text-sm font-medium text-navy-900" data-id="element-217">
                  CSV (.csv)
                </span>
                <span className="block text-xs text-navy-400" data-id="element-218">
                  Plain comma-separated data
                </span>
              </span>
            </button>
            <div className="mt-1 flex items-center gap-2 border-t border-navy-50 px-3 py-2 text-[11px] text-navy-400" data-id="element-219">
              {driveSynced ? <>
                  <CloudIcon className="h-3.5 w-3.5 text-accent-700" data-id="element-220" />
                  Will sync to Google Drive when ready
                </> : <>
                  <ClockIcon className="h-3.5 w-3.5" data-id="element-221" />
                  Queued for download — not downloaded directly
                </>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}